import React, { useEffect, useState } from 'react';

import { CircularProgress, Collapse, Typography } from '@mui/material';
import clsx from 'clsx';
import { ChatChevronIcon } from 'src/components/icons/ChatChevronIcon';
import { ToolCallResearchIcon } from 'src/components/icons/ToolCallResearchIcon';
import { makeStyles } from 'tss-react/mui';

import { toolIconProps, useToolCallStyles } from './CodeToolCallElement';
import { AlineMarkdown } from '../../AlineMarkdown';
import type { MarkdownComponentProps } from '../../ChatPanelMarkdown';
import { getCodeBlockInfoFromNode } from '../../remark-plugins/remarkCodeBlock';

const useThinkingStyles = makeStyles()(theme => ({
  thinkingContent: {
    '& p': {
      '&:not(:last-child)': {
        marginBottom: theme.spacing(1),
      },
      margin: 0,
    },
  },
}));

export const CodeThinkingElement = React.memo(function CodeThinkingElement({
  node,
}: MarkdownComponentProps): JSX.Element {
  const { classes: toolCallClasses } = useToolCallStyles();
  const { classes: thinkingStyles } = useThinkingStyles();
  const [open, setOpen] = useState<boolean>(false);
  const codeBlockInfo = getCodeBlockInfoFromNode(node);
  const { code = '', isNodeStreaming = false } = codeBlockInfo ?? {};

  useEffect(() => {
    setOpen(isNodeStreaming);
  }, [isNodeStreaming]);

  return (
    <div className={toolCallClasses.root}>
      <div className={toolCallClasses.row}>
        <div className={toolCallClasses.iconDiv}>
          <CodeThinkingIcon loading={isNodeStreaming} />
        </div>
        <div
          className={clsx(toolCallClasses.titleContainer, toolCallClasses.clickableTitleContainer)}
          onClick={() => setOpen(!open)}
        >
          <Typography
            className={clsx(
              toolCallClasses.title,
              isNodeStreaming && toolCallClasses.processingText
            )}
          >
            {isNodeStreaming ? 'Thinking...' : 'Thought process'}
          </Typography>
          <ChatChevronIcon
            height={14}
            style={open ? { transform: 'rotate(90deg)' } : undefined}
            width={14}
          />
        </div>
      </div>
      <div className={toolCallClasses.row}>
        <div className={toolCallClasses.iconDiv}>
          <div className={toolCallClasses.verticalLine} />
        </div>
        <div className={toolCallClasses.textDiv}>
          <Collapse in={open}>
            <div
              className={clsx(
                toolCallClasses.description,
                thinkingStyles.thinkingContent,
                isNodeStreaming && toolCallClasses.processingText
              )}
            >
              <AlineMarkdown markdownString={code} type="message" />
            </div>
          </Collapse>
        </div>
      </div>
    </div>
  );
});

function CodeThinkingIcon({ loading }: { loading: boolean }) {
  if (loading) return <CircularProgress size={14} />;

  return <ToolCallResearchIcon {...toolIconProps} />;
}
